import { Box, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatLanguage } from 'utils/format';
import { AppLanguage, appLanguages } from 'types/general';
import { useUpdateAdmin, useUpdateMe } from 'store/admins';
import { useAdminUpdateAdmin } from 'store/admin-admins';
import { usePredefinedToasts } from 'utils/Toast';
import { upper } from 'utils/general';
import { ReactComponent as Checkmark } from '../../../assets/icons/checkmark.svg';
import { ReactComponent as ChevronLeft } from '../../../assets/icons/chevron-left.svg';
import { useMe } from '../../../state/Administrators';
import { StorageKeys } from '../../../types/LocalStorage';
import useStyles from './LanguageSwitcher.styles';

interface IProps {
  handleBack: () => void;
}

export default function LanguageSwitcher(props: IProps) {
  const { me, isMelpAdmin, isHrAdmin } = useMe();
  const { handleBack } = props;
  const classes = useStyles();
  const { i18n, t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { mutate: updateAdmin, isLoading: isUpdating } = useUpdateAdmin();
  const { mutate: updateMe, isLoading: isUpdatingMe } = useUpdateMe();
  const { mutate: updateMelpAdmin, isLoading: isUpdatingMelpAdmin } =
    useAdminUpdateAdmin();
  const changeLanguage = async (language: Uppercase<AppLanguage>) => {
    await i18n.changeLanguage(language.toLowerCase());
    localStorage.setItem(StorageKeys.lang, language.toLowerCase());
    if (isHrAdmin && me) {
      updateAdmin(
        {
          adminId: me.id,
          data: { language },
        },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
          },
        },
      );
      updateMe({ data: { language } });
    }
    if (me && isMelpAdmin) {
      updateMelpAdmin({ adminId: me.id, data: { language } });
    }
    handleBack();
  };

  const languageMap = [...appLanguages].sort().map((language) => (
    <MenuItem
      classes={{ root: clsx(classes.vAlignCenter, classes.item) }}
      onClick={() => changeLanguage(upper(language))}
      disabled={isUpdating || isUpdatingMe || isUpdatingMelpAdmin}
      data-test={language}
      key={language}
    >
      <Typography
        variant="body2"
        color="textSecondary"
        className={clsx({
          [classes.bold]: i18n.language === language,
        })}
      >
        {formatLanguage(language, language)}
      </Typography>
      {i18n.language === language && <Checkmark className={classes.icon} />}
    </MenuItem>
  ));

  return (
    <>
      <MenuItem
        onClick={handleBack}
        className={clsx(classes.backItem, classes.item)}
      >
        <Box marginRight="11px">
          <ChevronLeft />
        </Box>
        <Typography variant="body2" color="textSecondary">
          {t('common.back')}
        </Typography>
      </MenuItem>
      {languageMap}
    </>
  );
}
